import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Flame } from '@sorare/core/src/atoms/icons/Flame';
import { Target } from '@sorare/core/src/atoms/icons/Target';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandM, BrandS } from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { TutorialDialog } from '@sorare/core/src/components/dialog/TutorialDialog';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { ANY_SPORT_SERIES_TASK_LINEUPS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { HotStreakTitle } from '#src/components/series/HotStreakTitle/index.tsx';

import type {
  HotStreaksTutorialStoryQuery,
  HotStreaksTutorialStoryQueryVariables,
  HotStreaksTutorialStory_tasksTrack,
} from './__generated__/index.graphql.ts';
import bg from './assets/bg.webp';

const HOT_STREAKS_TUTORIAL_STORY_QUERY = gql`
  query HotStreaksTutorialStoryQuery($taskId: ID!) {
    currentUser {
      slug
      task(id: $taskId) {
        id
        target
        ... on ThresholdPickerTask {
          id
          replay
          myFilteredBench(
            filters: { sortType: { direction: DESC, type: POPULARITY } }
            first: 5
          ) {
            nodes {
              id
              pictureUrl
              anyPlayer {
                slug
                activeClub {
                  slug
                }
                activeNationalTeam {
                  slug
                }
              }
              eligiblePlayerGameScores(taskId: $taskId) {
                id
                anyGame {
                  id
                  date
                  awayTeam {
                    slug
                    pictureUrl
                  }
                  homeTeam {
                    slug
                    pictureUrl
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${AnyRewardConfig.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<
  HotStreaksTutorialStoryQuery,
  HotStreaksTutorialStoryQueryVariables
>;

const StyledTutorialDialog = styled(TutorialDialog)`
  background: var(--c-black);
  @media ${tabletAndAbove} {
    overflow: hidden;
    max-height: 90vh;
  }
`;

const Step = styled(Vertical).attrs({ gap: 3 })`
  padding: 0 var(--double-unit);
  background: url(${bg}) no-repeat center center;
  background-size: cover;
  max-width: 100%;
  min-width: 0;
`;

const CardImage = styled.img`
  width: 100%;
`;

const TeamLogo = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  border-radius: 50%;
`;

const CardWrapper = styled(Vertical)`
  flex: 1;
  min-width: 80px;
  &:first-child,
  &:last-child {
    opacity: 0.4;
  }

  &:nth-child(2),
  &:nth-child(4) {
    min-width: 110px;
  }

  &:nth-child(3) {
    min-width: 140px;
  }
`;

const CardsRow = styled(Horizontal)`
  margin: auto calc(-1 * var(--double-unit));
  padding-bottom: 100px;
  overflow: hidden;
`;

const BoldWrapper = styled.span`
  font-weight: bold;
  color: var(--c-rewards-orange);
`;

const StepContent = styled(SBHorizontal)`
  flex: 1;
  background: var(--c-black);
  border: 1px solid var(--c-nd-200);
  border-radius: var(--unit);
  padding: var(--unit) var(--double-unit);
`;

const StyledBezel = styled(Bezel)`
  width: 100%;
`;

const HotStreaksTutorialContent = ({
  task,
}: {
  task: Extract<
    NonNullable<HotStreaksTutorialStoryQuery['currentUser']>['task'],
    { __typename: 'ThresholdPickerTask' }
  >;
}) => {
  const { myFilteredBench } = task;
  const { nodes } = myFilteredBench;

  return (
    <Vertical gap={6} grow>
      <Vertical center gap={2} className="text-center">
        <HotStreakTitle />
        <BodyL>
          <FormattedMessage
            defaultMessage="Score more than {points} PTS in <b>upcoming matches.</b>"
            values={{
              points: (
                <span>
                  <Target size={2} /> {''}
                  {task.target}
                </span>
              ),
              b: chunks => <BoldWrapper>{chunks}</BoldWrapper>,
            }}
          />
        </BodyL>
      </Vertical>
      <CardsRow gap={1} center>
        {nodes.map(card => {
          const game = card.eligiblePlayerGameScores?.at(0)?.anyGame;
          const playerTeamSlugs = [
            card.anyPlayer.activeClub?.slug,
            card.anyPlayer.activeNationalTeam?.slug,
          ].filter(Boolean);
          const teamPictureUrl = playerTeamSlugs.some(
            slug => slug === game?.homeTeam?.slug
          )
            ? game?.awayTeam?.pictureUrl
            : game?.homeTeam?.pictureUrl;
          return (
            <CardWrapper key={card.id} center gap={1}>
              <BrandS color="var(--c-rewards-orange)">
                <FormattedDate
                  value={game?.date}
                  weekday="short"
                  day="numeric"
                />
              </BrandS>
              <CardImage src={card.pictureUrl || ''} alt="" />
              <Horizontal gap={1} center>
                <BrandS color="var(--c-nd-600)">
                  <FormattedMessage {...fantasy.vs} />
                </BrandS>
                <TeamLogo src={teamPictureUrl || ''} alt="" />
              </Horizontal>
            </CardWrapper>
          );
        })}
      </CardsRow>
    </Vertical>
  );
};

type HotStreaksTrackContentProps = {
  tasksTrack: HotStreaksTutorialStory_tasksTrack;
};

const HotStreaksTrackContent = ({
  tasksTrack,
}: HotStreaksTrackContentProps) => {
  const { steps } = tasksTrack;
  const reversedSteps = [...steps].reverse();

  return (
    <Vertical gap={4} grow>
      <Vertical center gap={2} className="text-center">
        <HotStreakTitle />
        <BodyL>
          <FormattedMessage
            defaultMessage="Advance your streak of wins{br}to earn bigger rewards!"
            values={{ br: <br /> }}
          />
        </BodyL>
      </Vertical>
      <Vertical gap={2}>
        {reversedSteps.map((step, index) => {
          const stepNumber = steps.length - index;
          const isActive = index === reversedSteps.length - 1;
          return (
            <Horizontal
              key={step.id}
              gap={4}
              style={{ opacity: isActive ? 1 : 0.5 }}
            >
              <Horizontal center>
                <Flame
                  color={isActive ? undefined : 'var(--c-nd-400)'}
                  size={2.5}
                />
                <BrandS color="var(--c-white)" className="absolute">
                  {stepNumber}
                </BrandS>
              </Horizontal>
              <StyledBezel>
                <StepContent center>
                  <Horizontal gap={1} center>
                    {isActive ? <Target size={2} /> : null}
                    <BrandM color="var(--c-white)">{step.target}</BrandM>
                  </Horizontal>
                  <AnyRewardConfig
                    anyRewardConfig={step.rewardConfigs[0]}
                    variant={Variant.TEXT}
                    textVariantSize="lg"
                  />
                </StepContent>
              </StyledBezel>
            </Horizontal>
          );
        })}
      </Vertical>
    </Vertical>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
  taskTrack: Nullable<HotStreaksTutorialStory_tasksTrack>;
};

export const HotStreaksTutorialStory = ({
  open,
  onClose,
  taskTrack,
}: Props) => {
  const navigate = useNavigate();
  const { update: updateLifecycle } = useLifecycle();
  const { generateSportContextPath } = useSportContext();

  const taskId = taskTrack?.steps?.at(0)?.id;

  const { data } = useQuery(HOT_STREAKS_TUTORIAL_STORY_QUERY, {
    variables: { taskId: idFromObject(taskId) || '' },
    skip: !taskId,
  });
  const task = isType(data?.currentUser?.task, 'ThresholdPickerTask')
    ? data.currentUser.task
    : undefined;

  if (!task || !taskTrack) {
    return null;
  }
  return (
    <StyledTutorialDialog
      open={open}
      onClose={() => {
        updateLifecycle(LIFECYCLE.sawSeriesHotStreakOnboarding, true);
        onClose();
      }}
      confirmCtaProps={{
        onClick: () => {
          updateLifecycle(LIFECYCLE.sawSeriesHotStreakOnboarding, true);
          navigate(
            generateSportContextPath(ANY_SPORT_SERIES_TASK_LINEUPS, {
              params: { task: idFromObject(task.id) },
            })
          );
        },
      }}
      disableSkip
    >
      <Step key="hot-streaks-tutorial-step1">
        <HotStreaksTutorialContent task={task} />
      </Step>
      <Step key="hot-streaks-tutorial-step2">
        <HotStreaksTrackContent tasksTrack={taskTrack} />
      </Step>
    </StyledTutorialDialog>
  );
};

HotStreaksTutorialStory.fragments = {
  tasksTrack: gql`
    fragment HotStreaksTutorialStory_tasksTrack on TasksTrack {
      id
      steps {
        id
        target
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<HotStreaksTutorialStory_tasksTrack>,
};
